/* eslint-disable @typescript-eslint/no-unused-vars */
// MenuData.ts
import {
  isExtenderPopEnabled,
  isInsightTopologyV2Enabled,
} from "@shared/config/featureFlags";
import { ReactComponent as HomeSvg } from "@assets/Icons/Icon.svg";
import { ReactComponent as IconReflector } from "@assets/Icons/IconReflector.svg";

// Kontrol menu icons
import { ReactComponent as ServerSVG } from "@assets/Icons/IconServer.svg";
import { ReactComponent as BackboneSVG } from "@assets/Icons/IconBackbone.svg";
import { ReactComponent as ConnectivityKontrolSVG } from "@assets/Icons/IconConnectivityKontrol.svg";
import { ReactComponent as DataInterconnectSVG } from "@assets/Icons/IconDataInterconnect.svg";
import { ReactComponent as InternetSVG } from "@assets/Icons/IconInternet.svg";
import { ReactComponent as IconFirewall } from "@assets/Icons/IconFwaas.svg";
import { ReactComponent as IconSdwan } from "@assets/Icons/Icon_sdwan.svg";
import { ReactComponent as IconBuilding } from "@assets/Icons/IconBuilding.svg";
import { ReactComponent as IconWarning } from "@assets/Icons/Icon_Warning.svg";

// Settings icons
// import { ReactComponent as BillingSVG } from "@assets/Icons/IconBilling.svg";
import { ReactComponent as LogsSVG } from "@assets/Icons/IconLogs.svg";
import { ReactComponent as SupportSVG } from "@assets/Icons/IconSupport.svg";
import { ReactComponent as Quotation } from "@assets/Icons/IconQuotation.svg";
import { ReactComponent as ReportSVG } from "@assets/Icons/IconReport.svg";
import { ReactComponent as IconKey } from "@assets/Icons/IconKey.svg";
import { ReactComponent as IconLicense } from "@assets/Icons/Icon_FileSign.svg";
import { ReactComponent as IconEyeSmall } from "@assets/Icons/SmallEye.svg";
import { ReactComponent as IconProfile2User } from "@assets/Icons/profile-2user.svg";
import { ReactComponent as IconPeople } from "@assets/Icons/people.svg";
import { ReactComponent as IconTag } from "@assets/Icons/IconTag.svg";
import { ReactComponent as IconNotifications } from "@assets/Icons/IconNotifications.svg";
import { ReactComponent as ProfileCircle } from "@assets/profile-circle.svg";

// === CTX Reflector V2 Menu Items ===
// import { settingsItems } from "./MenuData";

export interface MenuItem {
  label: string;
  id?: string;
  path?: string;
  Icon?: React.FC<React.SVGProps<SVGSVGElement>>;
  children?: MenuItem[];
  badge?: string;
  /**
   * Static label (e.g. prototype) shown next to the row badge; not replaced by
   * dynamic count.
   *
   * **Only for states that have no expiry date** — `Beta` lasts as long as the
   * feature is in beta, and someone deleting it is a deliberate act. A `New`
   * does not belong here: it is a claim with a shelf life, and this field has
   * no clock, so it stays lit until a person remembers. That is exactly what
   * happened to Network Topology — GA on 2026-08-06, still announcing itself
   * as new five weeks later (CTX-502).
   *
   * `New` is derived instead, from the entry's `releasedAt` and its
   * `walkthrough.path`, and it expires on its own — see
   * `useChangelogNewItems.ts` (CTX-511). `MenuData.badges.test.ts` keeps this
   * field from growing another one.
   */
  badgeBeta?: string;
  customAction?: () => void;
}

export const mainItems: MenuItem[] = [
  { label: "Home", id: "home", path: "/", Icon: HomeSvg },
];

/** Kontrol Edge — shown under Main (below Overview), not under Services. */
export const reportsMenuItem: MenuItem = {
  label: "Reports",
  id: "reports-root",
  Icon: ReportSVG,
  path: "/kontrol/reports",
};

/** Kontrol Edge — "Events & Reports" section items. */
export const eventManagementItems: MenuItem[] = [
  {
    label: "Reports",
    id: "reports-root",
    Icon: ReportSVG,
    path: "/kontrol/reports",
  },
  {
    label: "Alerts",
    id: "kontrol-alerts",
    Icon: IconWarning,
    path: "/kontrol/alerts",
    badgeBeta: "Beta",
  },
  {
    label: "Internet monitoring",
    id: "kontrol-internet-ip-monitoring",
    Icon: InternetSVG,
    path: "/kontrol/internet-ip-monitoring",
    badgeBeta: "Beta",
  },
];

// Aquí quitamos el "Management" de dentro de CTX Reflector.
// Dejamos "Main Status" y "L3 Overview" como hijos directos.
export const serviceItems: MenuItem[] = [
  {
    label: "CTX Hub",
    id: "ctx-hub",
    children: [
      { label: "Data Usage", id: "ctx-hub-status", path: "/ctxhub/status" },
    ],
  },
  {
    label: "Ports",
    id: "kontrol-port",
    path: "/kontrol/port",
    Icon: ServerSVG,
  },
  {
    label: "Internet connectivity",
    id: "kontrol-ipx",
    path: "/kontrol/ipx",
    Icon: InternetSVG,
  },
  {
    label: "DC interconnect",
    id: "kontrol-dc",
    path: "/kontrol/datacenter",
    Icon: DataInterconnectSVG,
  },
  {
    label: "Cloud connectivity",
    id: "kontrol-cc",
    path: "/kontrol/cloud-connectivity",
    Icon: ConnectivityKontrolSVG,
  },
  {
    label: "Backbone",
    id: "kontrol-backbone",
    path: "/kontrol/backbone",
    Icon: BackboneSVG,
  },
  {
    label: "Virtual firewall",
    id: "fwaas-root",
    Icon: IconFirewall,
    path: "/kontrol/fwaas",
  },
  {
    label: "SD-WAN",
    id: "sdwan-hub-root",
    Icon: IconSdwan,
    path: "/kontrol/sdwan-hub",
  },
  {
    label: "CTX Reflector",
    id: "ctx-reflector",
    Icon: IconReflector,
    children: [
      
      {
        label: "L3 Overview",
        id: "reflector-l3overview",
        children: [
          {
            label: "BGP Overview",
            id: "reflector-l3overview-bgp",
            path: "/reflector/l3overview/bgp",
          },
          {
            label: "Logs Detail",
            id: "reflector-l3overview-logs",
            path: "/reflector/l3overview/logs",
          },
        ],
      },
      // <-- Ya NO incluimos "Management" aquí
    ],
  },
  // Extended PoP closes the Services list by request (CTX-362): the
  // product extends the PoP into the customer site, so it sits after the
  // per-service entries rather than among them. BETA: hidden in production
  // via isExtenderPopEnabled(), which also gates its routes.
  ...(isExtenderPopEnabled()
    ? [
        {
          label: "Extended PoP",
          id: "kontrol-extender-pop",
          path: "/kontrol/extender-pop",
          Icon: IconBuilding,
        },
      ]
    : []),
  // {
  //   label: "Quotations",
  //   id: "quotations",
  //   Icon: Quotation,
  //   path: "/kontrol/quotations",
  // },
];

// NUEVO: sección "Management" top-level
export const managementItems: MenuItem[] = [
  {
    label: "Licensing",
    id: "reflector-management-licensing",
    path: "/reflector/management/licensing",
  },
  {
    label: "Instances",
    id: "reflector-management-instances",
    children: [
      {
        label: "Licensed",
        id: "reflector-instances-licensed",
        path: "/reflector/management/instances/licensed",
      },
      {
        label: "Unlicensed",
        id: "reflector-instances-unlicensed",
        path: "/reflector/management/instances/unlicensed",
      },
      {
        label: "High Availability",
        id: "reflector-instances-highavailability",
        path: "/reflector/management/instances/highavailability",
      },
      {
        label: "Health Check",
        id: "reflector-instances-healthcheck",
        path: "/reflector/management/instances/healthcheck",
      },
      {
        label: "Syslog",
        id: "reflector-instances-syslog",
        path: "/reflector/management/instances/syslog",
      },
    ],
  },
  {
    label: "BGP",
    id: "reflector-management-bgp",
    children: [
      {
        label: "Neighbors",
        id: "reflector-bgp-management",
        path: "/reflector/management/bgp/neighbors",
      },
      {
        label: "Status",
        id: "reflector-bgp-status",
        path: "/reflector/management/bgp/status",
      },
    ],
  },
  {
    label: "Syslog",
    id: "reflector-management-reporting",
    path: "/reflector/reporting",
  },
];


// === Account area (shared across all products) ===

/**
 * The rail's closing "Account" section, appended to every product's nav by
 * `SideMenuNavByProduct` (CTX-512, completed in CTX-581).
 *
 * These seven rows used to be four separate lists (Customers / Administration /
 * Personal Settings / the gated `account-management` leftover) rendered inside
 * the Settings drill-in, which meant the product nav disappeared while you used
 * them. They are ordinary nav rows now, and the grouping that justified the
 * split is gone with the panel that hosted it.
 *
 * Order is personal first, administrative after — the rows every tenant type
 * sees lead, and the GLOBAL_ADMIN-only ones trail, so a customer's section ends
 * where their access does instead of opening with gaps.
 *
 * Per-item visibility is decided in exactly one place, `filterAccountAreaItems`
 * in `sideMenuVisibility.ts`, keyed by these ids. Ids are load-bearing:
 * renaming one silently unhides its row. Every row carries an `Icon` because
 * the collapsed rail renders the glyph alone.
 */
export const accountAreaItems: MenuItem[] = [
  {
    label: "Profile",
    id: "account-management-profile",
    path: "/account/profile",
    Icon: ProfileCircle,
  },
  {
    label: "Notifications",
    id: "account-management-notifications",
    path: "/account/notifications",
    Icon: IconNotifications,
    badgeBeta: "Beta",
  },
  {
    label: "Tags",
    id: "account-management-tags",
    path: "/account/tags-manager",
    Icon: IconTag,
    badgeBeta: "Beta",
  },
  {
    label: "Users",
    id: "account-management-users",
    path: "/account/users",
    Icon: IconProfile2User,
  },
  {
    label: "Customers",
    id: "account-management-customers",
    path: "/account/customers",
    Icon: IconPeople,
  },
  {
    // The `id` stays `…-integrations` on purpose: the prod/BETA gate keys off
    // it, and renaming it would silently unhide the row in production (CTX-37).
    label: "API",
    id: "account-management-integrations",
    path: "/account/api",
    Icon: IconKey,
    badgeBeta: "Beta",
  },
  {
    // Audit Logs sits with the account rows, not under Support: it reads as
    // "my tenant's activity", and its route living under `/support` is an
    // accident of where the page was first mounted.
    label: "Audit Logs",
    id: "support-audit-logs",
    path: "/support/audit-logs",
    Icon: LogsSVG,
    badgeBeta: "Beta",
  },
];

/** Help — support entry points. */
export const helpItems: MenuItem[] = [
  {
    label: "Technical Support",
    id: "request-root",
    path: "/support/request",
    Icon: SupportSVG,
  },
];

// === CTX Reflector V2 Menu Items ===
export const reflectorV2MenuItems: MenuItem[] = [
  { label: "Overview", id: "reflector-v2-overview", path: "/reflector-v2/overview", Icon: HomeSvg },
  {
    label: "Management",
    id: "reflector-v2-management",
    children: [
      { label: "Reflectors", id: "reflector-v2-bgp-reflectors", path: "/reflector-v2/management/bgp-reflectors" , Icon: ServerSVG},
      { label: "BGP Visibility", id: "reflector-v2-bgp-visibility", path: "/reflector-v2/management/bgp-visibility", Icon: IconEyeSmall },
      // CTX-97 — the source-first Network Topology product launches inside
      // Reflector V2 (moved from the Insight menu, 2026-08-04); a plain
      // Management entry, no section of its own. GA in every environment
      // since 2026-08-06 — `isInsightTopologyV2Enabled()` is kept as the
      // single switch in case it ever needs turning off in a hurry.
      ...(isInsightTopologyV2Enabled()
        ? [
            {
              label: "Network Topology",
              id: "reflector-v2-network-topology",
              path: "/reflector-v2/network-topology",
              Icon: ConnectivityKontrolSVG,
            },
          ]
        : []),
      { label: "Licensing", id: "reflector-v2-licensing", path: "/reflector-v2/management/licensing" , Icon: IconLicense},
      { label: "Syslog", id: "reflector-v2-reporting", path: "/reflector-v2/reporting", Icon: ReportSVG },
      { label: "Logs", id: "reflector-v2-logs", path: "/reflector-v2/logs", Icon: LogsSVG },
    ],
  },
  // Settings-area sections (Customers / Administration / Personal Settings /
  // Account / Help) are appended for every product by SideMenuNavByProduct,
  // so they are no longer embedded here.
];

// === CTX Insight Menu Items ===
export const insightMenuItems: MenuItem[] = [
  {
    label: "Main",
    id: "insight-main",
    children: [
      { label: "Home", id: "insight-home", path: "/insight/home", Icon: HomeSvg },
      { label: "Network Topology", id: "insight-network-topology", path: "/insight/topology", Icon: ConnectivityKontrolSVG },
      { label: "Optimization Hub", id: "insight-optimization", path: "/insight/optimization", Icon: Quotation },
    ],
  },
  // Settings-area sections are appended for every product by
  // SideMenuNavByProduct, so they are no longer embedded here.
];
